import { useEffect, useRef } from 'react';
import type { GameState, PlayerRoundResult } from '../types';
import {
  emitLifecycleEventOnce,
  roundCompleteEvent,
} from '../lib/productAnalytics';

interface ProductAnalyticsTrackerProps {
  gameState: GameState;
  roundResults: PlayerRoundResult[];
  isOnline?: boolean;
}

let cpuSessionSequence = 0;

function nextCpuSessionKey(): string {
  cpuSessionSequence += 1;
  return `${Date.now().toString(36)}-${cpuSessionSequence.toString(36)}`;
}

export default function ProductAnalyticsTracker({
  gameState,
  roundResults,
  isOnline = false,
}: ProductAnalyticsTrackerProps) {
  const previousPhaseRef = useRef(gameState.phase);
  const gameStartedAtRef = useRef(Date.now());
  const gameSessionKeyRef = useRef(nextCpuSessionKey());
  const roundNumberRef = useRef(0);

  useEffect(() => {
    const previousPhase = previousPhaseRef.current;

    if (isOnline) {
      previousPhaseRef.current = gameState.phase;
      return;
    }

    if (gameState.phase === 'playing' && previousPhase === 'gameOver') {
      gameStartedAtRef.current = Date.now();
      gameSessionKeyRef.current = nextCpuSessionKey();
      roundNumberRef.current = 0;
    }

    const enteredSummary = (
      gameState.phase === 'roundEnd' || gameState.phase === 'gameOver'
    ) && previousPhase !== 'roundEnd' && previousPhase !== 'gameOver';

    if (enteredSummary) {
      roundNumberRef.current += 1;
      const completedAt = Date.now();
      emitLifecycleEventOnce(roundCompleteEvent({
        mode: 'cpu',
        roundKey: `${gameSessionKeyRef.current}:${roundNumberRef.current}`,
        players: gameState.numPlayers,
        roundScore: roundResults[0]?.score,
        totalScore: gameState.players[0]?.totalScore,
        gameStartedAt: gameStartedAtRef.current,
        completedAt,
      }));
    }

    previousPhaseRef.current = gameState.phase;
  }, [
    gameState.phase,
    gameState.numPlayers,
    gameState.players,
    isOnline,
    roundResults,
  ]);

  return null;
}
